import { StateManager } from '../../managers';
import { rarityColor } from './TooltipContent';
import { renderItemIcon } from './ItemIcon';
import { Tooltip } from './Tooltip';
import { buildItemTooltip } from './TooltipContent';
import { formatUnitClass } from './GameTerms';
import { getFullSpriteUrl } from '../../combat/spriteConfig';
import type { ItemData, ConfigData, UnitData, PartyUnit } from '../../types';

export interface ItemPickerOptions {
  /** Header title shown at the top of the panel. */
  title: string;
  /** Pre-filtered list of items to show. Caller decides eligibility. */
  items: ItemData[];
  /** When set, renders a sticky "Crafting for" strip + biases default group to this strand. */
  activeStrand?: PartyUnit | null;
  /** Caller invoked when a card is picked. */
  onPick: (item: ItemData) => void;
  /** Optional close callback (after pick or dismiss). */
  onClose?: () => void;
  /** Pre-select this item id with the gold accent border. */
  selectedId?: string;
  /** Shown in the empty-state placeholder when items is empty (or all filtered out). */
  emptyMessage?: string;
  /**
   * Optional per-item check. Return a short reason string ("Requires Fortification 3")
   * if the active strand can't equip this item; the card renders dim+red and the meta
   * line shows the reason. Return null to mark the item as equippable. Click is still
   * allowed — server has final say.
   */
  isItemUnequippable?: (item: ItemData) => string | null;
}

let activeOverlay: HTMLElement | null = null;
let escHandler: ((e: KeyboardEvent) => void) | null = null;

const RARITY_FILTERS: { value: string; label: string; color?: string }[] = [
  { value: 'all',    label: 'All' },
  { value: 'normal', label: 'Normal', color: '#bbb' },
  { value: 'magic',  label: 'Magic',  color: '#88bbff' },
  { value: 'rare',   label: 'Rare',   color: '#ffd966' },
  { value: 'unique', label: 'Unique', color: '#c8a86e' },
];

function close(): void {
  if (!activeOverlay) return;
  const overlay = activeOverlay;
  overlay.classList.remove('is-visible');

  if (escHandler) {
    document.removeEventListener('keydown', escHandler);
    escHandler = null;
  }

  setTimeout(() => {
    overlay.remove();
    if (activeOverlay === overlay) activeOverlay = null;
  }, 350);
}

function open(options: ItemPickerOptions): void {
  if (activeOverlay) close();

  const { title, items, activeStrand = null, onPick, onClose, selectedId, emptyMessage, isItemUnequippable } = options;
  const emptyText = emptyMessage ?? 'No items match.';
  const configData = StateManager.get('configData') as ConfigData | undefined;
  const unitData = StateManager.get('unitData') as UnitData | undefined;
  const roster = unitData?.roster ?? [];

  // Active-strand strip (mirrors RosterPicker's pairing strip)
  const activeStripHTML = activeStrand
    ? buildActiveStrip(activeStrand, configData)
    : '';

  // Build filter bar — rarity dimension only for V1; slot can come later if needed
  const rarityButtons = RARITY_FILTERS.map(r => {
    const styleAttr = r.color ? ` style="--filter-color:${r.color}"` : '';
    const activeCls = r.value === 'all' ? ' roster-filter-active' : '';
    return `<button class="roster-filter-btn${activeCls}" data-filter-dim="rarity" data-filter-value="${r.value}"${styleAttr}>${r.label}</button>`;
  }).join('');

  // Group items by owner strand (or "Unequipped"). Active strand pinned to top.
  const groups = groupItemsByOwner(items, roster, activeStrand?.id ?? null);
  const cardsHTML = groups.map(g => {
    const headerColor = g.unit ? rarityColor(g.unit.rarity ?? 'common') : 'rgba(255,255,255,0.4)';
    const headerLabel = g.unit
      ? `<span class="ip-group-name" style="color:${headerColor}">${g.unit.name}</span><span class="ip-group-class">${formatUnitClass(g.unit.unitClass)}</span>`
      : `<span class="ip-group-name">Unequipped</span>`;
    const cardCount = g.items.length;
    const itemsHTML = g.items.map(item => buildItemCard(item, configData, selectedId, isItemUnequippable?.(item) ?? null)).join('');
    return `
      <div class="ip-group" data-group-id="${g.unit?.id ?? 'unequipped'}">
        <div class="ip-group-header">${headerLabel}<span class="ip-group-count">${cardCount}</span></div>
        <div class="ip-group-grid">${itemsHTML}</div>
      </div>
    `;
  }).join('');

  const overlay = document.createElement('div');
  overlay.className = 'roster-overlay item-picker-overlay';
  overlay.innerHTML = `
    <div class="roster-overlay-backdrop roster-backdrop"></div>
    <div class="roster-overlay-panel">
      <div class="roster-overlay-header">
        <span class="roster-overlay-title">${title}</span>
        <button class="roster-close-btn roster-overlay-close" type="button">✕</button>
      </div>
      ${activeStripHTML}
      <div class="roster-filter-bar">${rarityButtons}</div>
      <div class="roster-overlay-grid item-picker-grid">
        ${cardsHTML || `<div class="ip-empty">${emptyText}</div>`}
      </div>
    </div>
  `;

  document.body.appendChild(overlay);
  activeOverlay = overlay;
  requestAnimationFrame(() => overlay.classList.add('is-visible'));

  const handleClose = () => {
    Tooltip.hide();
    close();
    onClose?.();
  };

  overlay.querySelector('.roster-close-btn')?.addEventListener('click', handleClose);
  overlay.querySelector('.roster-backdrop')?.addEventListener('click', handleClose);

  escHandler = (e: KeyboardEvent) => { if (e.key === 'Escape') handleClose(); };
  document.addEventListener('keydown', escHandler);

  // Filter buttons
  overlay.querySelectorAll<HTMLButtonElement>('.roster-filter-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const dim = btn.dataset.filterDim ?? 'rarity';
      overlay.querySelectorAll<HTMLButtonElement>(`.roster-filter-btn[data-filter-dim="${dim}"]`)
        .forEach(b => b.classList.remove('roster-filter-active'));
      btn.classList.add('roster-filter-active');
      applyFilters(overlay);
    });
  });

  // Card clicks + tooltips
  overlay.querySelectorAll<HTMLElement>('.ip-card').forEach(card => {
    const id = card.dataset.itemId!;
    const item = items.find(i => i.id === id);
    if (!item) return;

    Tooltip.attach(card, () => buildItemTooltip(item, configData));

    card.addEventListener('click', () => {
      Tooltip.hide();
      onPick(item);
      close();
      onClose?.();
    });
  });
}

function buildActiveStrip(unit: PartyUnit, configData: ConfigData | undefined): string {
  const template = configData?.unitTemplates?.[unit.unitClass];
  const skinKey = unit.activeSkinId && configData?.skins?.[unit.activeSkinId]?.spriteKey;
  const spriteKey = skinKey || template?.spriteKey || unit.unitClass;
  const spriteSrc = getFullSpriteUrl(spriteKey);
  const rColor = rarityColor(unit.rarity ?? 'common');
  const className = formatUnitClass(unit.unitClass);
  return `
    <div class="roster-pairing-strip">
      <span class="roster-pairing-label">Crafting for</span>
      <div class="roster-pairing-card">
        <img class="roster-pairing-sprite" src="${spriteSrc}" alt="${unit.name}" draggable="false">
        <div class="roster-pairing-info">
          <span class="roster-pairing-name" style="color:${rColor}">${unit.name}</span>
          <span class="roster-pairing-meta">Lv.${unit.level} ${className}</span>
        </div>
      </div>
    </div>
  `;
}

interface ItemGroup {
  unit: PartyUnit | null; // null = unequipped
  items: ItemData[];
}

function groupItemsByOwner(items: ItemData[], roster: PartyUnit[], pinnedUnitId: string | null): ItemGroup[] {
  const groups = new Map<string, ItemGroup>();
  const unequipped: ItemGroup = { unit: null, items: [] };

  for (const item of items) {
    if (!item.equippedToUnitId) {
      unequipped.items.push(item);
      continue;
    }
    const unit = roster.find(u => u.id === item.equippedToUnitId);
    if (!unit) {
      unequipped.items.push(item);
      continue;
    }
    let group = groups.get(unit.id);
    if (!group) {
      group = { unit, items: [] };
      groups.set(unit.id, group);
    }
    group.items.push(item);
  }

  // Sort items inside each group by inventorySlot then name
  for (const g of [...groups.values(), unequipped]) {
    g.items.sort((a, b) => (a.inventorySlot ?? 999) - (b.inventorySlot ?? 999) || a.name.localeCompare(b.name));
  }

  // Order: pinned unit first, then other equipped strands by name, then unequipped last
  const ordered: ItemGroup[] = [];
  if (pinnedUnitId && groups.has(pinnedUnitId)) {
    ordered.push(groups.get(pinnedUnitId)!);
    groups.delete(pinnedUnitId);
  }
  const remainingUnits = [...groups.values()].sort((a, b) =>
    (a.unit?.name ?? '').localeCompare(b.unit?.name ?? '')
  );
  ordered.push(...remainingUnits);
  if (unequipped.items.length > 0) ordered.push(unequipped);

  return ordered;
}

function buildItemCard(item: ItemData, configData: ConfigData | undefined, selectedId: string | undefined, unequippableReason: string | null): string {
  const rColor = rarityColor(item.rarity ?? 'normal');
  const stripRarity = (name: string) => name.replace(/^(Magic|Rare|Epic|Legendary)\s+/i, '');
  const iconHtml = renderItemIcon(item.baseType, item.category, 'md');
  const slotLabel = formatSlot(item.category);
  const isSelected = item.id === selectedId;
  const selectedCls = isSelected ? ' ip-card--selected' : '';
  const lockCls = unequippableReason ? ' ip-card--unequippable' : '';
  // When unequippable, the meta line shows WHY (e.g. "Requires Fortification 3")
  // instead of the slot label — players already know they tapped Equip Offhand.
  const metaText = unequippableReason ?? slotLabel;
  const metaCls = unequippableReason ? 'ip-card-meta ip-card-reason' : 'ip-card-meta';

  return `
    <div class="ip-card${selectedCls}${lockCls}" data-item-id="${item.id}" data-rarity="${item.rarity ?? 'normal'}" data-slot="${item.category}" style="--rarity-color:${rColor}">
      <div class="ip-card-icon">${iconHtml}</div>
      <div class="ip-card-info">
        <span class="ip-card-name" style="color:${rColor}">${stripRarity(item.name)}</span>
        <span class="${metaCls}">${metaText}</span>
      </div>
    </div>
  `;
}

function formatSlot(category: string): string {
  switch (category) {
    case 'weapon':    return 'Weapon';
    case 'armor':     return 'Armor';
    case 'offhand':   return 'Offhand';
    case 'accessory': return 'Accessory';
    default:          return category;
  }
}

function applyFilters(overlay: HTMLElement): void {
  const activeRarity = overlay.querySelector<HTMLButtonElement>('.roster-filter-btn[data-filter-dim="rarity"].roster-filter-active')?.dataset.filterValue ?? 'all';

  overlay.querySelectorAll<HTMLElement>('.ip-group').forEach(group => {
    let visibleCount = 0;
    group.querySelectorAll<HTMLElement>('.ip-card').forEach(card => {
      const cardRarity = card.dataset.rarity ?? 'normal';
      const passRarity = activeRarity === 'all' || cardRarity === activeRarity;
      card.style.display = passRarity ? '' : 'none';
      if (passRarity) visibleCount++;
    });
    group.style.display = visibleCount > 0 ? '' : 'none';
  });
}

export const ItemPicker = { open, close };
